/**
 * ValueSliderInput — synced big numeric input + range slider.
 * One controlled value source; both inputs stay in sync and are clamped.
 */
import { useCallback } from 'react';
import clsx from 'clsx';
import { Input } from '../../components/ui/Input';

export interface ValueSliderInputProps {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  className?: string;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

export function ValueSliderInput({
  value,
  onChange,
  min,
  max,
  step = 0.01,
  unit = '',
  className,
}: ValueSliderInputProps) {
  const handleNumber = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = parseFloat(e.target.value);
      if (Number.isFinite(raw)) onChange(clamp(raw, min, max));
    },
    [onChange, min, max],
  );

  const handleSlider = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(clamp(parseFloat(e.target.value), min, max));
    },
    [onChange, min, max],
  );

  return (
    <div className={clsx('flex flex-col gap-3', className)}>
      <div className="flex items-baseline gap-2">
        <Input
          type="number"
          inputMode="decimal"
          aria-label="Value"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={handleNumber}
          className="num text-4xl font-bold text-accent w-36 text-center"
        />
        {unit && <span className="text-text-2 text-xl font-medium">{unit}</span>}
      </div>
      <input
        type="range"
        aria-label="Value slider"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={handleSlider}
        className="w-full accent-accent"
      />
      <div className="flex justify-between text-xs text-text-3">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}
